import { useCallback, useEffect, useMemo, useState } from 'react';
import { griglie } from '../../api/griglie';
import type {
  DefinizioneColonna,
  FiltroColonna,
  MetadatiGriglia,
  Ordinamento,
  RichiestaGriglia,
  RisultatoGriglia,
} from '../../lib/tipi';
import { etichettaTipo, TIPI_NUMERICI } from '../../lib/tipi';
import {
  formattaData,
  formattaDataOra,
  formattaImporto,
  formattaNumero,
  formattaPercentuale,
} from '../../lib/formato';
import { tonoStato } from '../../lib/stati';
import { ChipFiltri } from './ChipFiltri';
import { MenuFiltro } from './MenuFiltro';

/**
 * Selezione multipla a spunta. Gli identificativi li tiene chi usa la griglia, non la
 * griglia: una spunta messa a pagina 1 deve sopravvivere a un filtro e a un cambio di
 * pagina, e se la lista vivesse qui dentro si perderebbe al primo ricaricamento.
 */
export interface SelezioneGriglia {
  selezionati: string[];
  onCambia: (identificativi: string[]) => void;
}

interface Props {
  nomeGriglia: string;
  /** Colonne da mostrare, nell'ordine. Assente = tutte quelle dichiarate dal backend. */
  colonneVisibili?: string[];
  intestazioni?: Record<string, string>;
  dimensionePagina?: number;
  /** Se presente, le righe diventano selezionabili con il mouse e da tastiera. */
  onRigaSelezionata?: (riga: Record<string, unknown>) => void;
  /** Id della riga attualmente aperta, per tenerla evidenziata mentre si guarda il dettaglio. */
  idSelezionato?: string | null;
  /** Se presente, compare la colonna delle spunte. */
  selezione?: SelezioneGriglia;
  /** Ricaricamento forzato: cambia questo valore e la griglia rilegge le righe. */
  versione?: number;
  /*  Colonne da disegnare come pastiglia colorata invece che come testo.

      Quali siano lo decide la schermata e non il backend: il tipo del dato e' Elenco in
      entrambi i casi, quello che cambia e' se il valore va letto in un colpo d'occhio
      scorrendo cento righe — lo stato di un documento — o solo letto. Il colore esce da
      `tonoStato`, che e' la stessa scala usata dal ciclo di vita nelle schede.  */
  pastiglie?: string[];
  /*  Filtri con cui la griglia si apre AL POSTO di quelli predefiniti del backend.
      Servono alle viste pronte di una schermata — «in attesa», «le mie» — che sono
      filtri come gli altri: si vedono fra i chip e si tolgono. Si leggono una volta,
      al caricamento; per cambiarli la schermata rimonta la griglia con una `key` nuova.  */
  filtriIniziali?: FiltroColonna[];
}

const VUOTO: RichiestaGriglia = { pagina: 1, dimensione: 50, filtri: [], ordinamenti: [] };

export function DataGrid({
  nomeGriglia,
  colonneVisibili,
  intestazioni = {},
  dimensionePagina = 50,
  onRigaSelezionata,
  idSelezionato,
  selezione,
  versione = 0,
  pastiglie,
  filtriIniziali,
}: Props) {
  const [metadati, setMetadati] = useState<MetadatiGriglia | null>(null);
  const [richiesta, setRichiesta] = useState<RichiestaGriglia>({
    ...VUOTO,
    dimensione: dimensionePagina,
  });
  const [risultato, setRisultato] = useState<RisultatoGriglia | null>(null);
  const [totaleNonFiltrato, setTotaleNonFiltrato] = useState<number>();
  /*  Non basta il nome della colonna: al menu serve anche il bottone sotto cui aprirsi,
      perche' da quando si disegna fuori dalla tabella le coordinate se le deve calcolare.  */
  const [menuAperto, setMenuAperto] = useState<{ colonna: string; ancora: HTMLElement } | null>(
    null,
  );
  const [errore, setErrore] = useState<string | null>(null);
  const [inCaricamento, setInCaricamento] = useState(false);

  useEffect(() => {
    griglie
      .metadati(nomeGriglia)
      .then((m) => {
        setMetadati(m);
        // I filtri predefiniti entrano una volta sola, qui: diventano filtri normali,
        // visibili fra i chip e togliibili. Riapplicarli a ogni giro li renderebbe
        // impossibili da levare.
        const iniziali = filtriIniziali ?? m.filtriPredefiniti;
        if (iniziali?.length) setRichiesta((precedente) => ({ ...precedente, filtri: iniziali }));
      })
      .catch((e: Error) => setErrore(e.message));
  // `filtriIniziali` fuori dalle dipendenze apposta: si leggono una volta sola.
  }, [nomeGriglia]);

  // Il totale senza filtri serve al "312 righe → 34": senza il riferimento
  // il conteggio filtrato non dice quanto si è ristretto.
  useEffect(() => {
    if (!metadati) return;
    griglie
      .righe(nomeGriglia, { ...VUOTO, dimensione: 1 })
      .then((r) => setTotaleNonFiltrato(r.totaleRighe))
      .catch(() => setTotaleNonFiltrato(undefined));
  }, [metadati, nomeGriglia, versione]);

  useEffect(() => {
    if (!metadati) return;
    let annullato = false;
    setInCaricamento(true);
    griglie
      .righe(nomeGriglia, richiesta)
      .then((r) => {
        if (!annullato) {
          setRisultato(r);
          setErrore(null);
        }
      })
      .catch((e: Error) => !annullato && setErrore(e.message))
      .finally(() => !annullato && setInCaricamento(false));
    return () => {
      annullato = true;
    };
  }, [metadati, nomeGriglia, richiesta, versione]);

  const colonne = useMemo<DefinizioneColonna[]>(() => {
    if (!metadati) return [];
    if (!colonneVisibili) return metadati.colonne;
    return colonneVisibili
      .map((nome) => metadati.colonne.find((c) => c.nome === nome))
      .filter((c): c is DefinizioneColonna => c !== undefined);
  }, [metadati, colonneVisibili]);

  const caricaValori = useCallback(
    (colonna: string) => () => griglie.valoriDistinti(nomeGriglia, colonna, richiesta),
    [nomeGriglia, richiesta],
  );

  function applicaFiltro(colonna: string, filtro: FiltroColonna | null) {
    setRichiesta((precedente) => ({
      ...precedente,
      pagina: 1,
      filtri: [
        ...precedente.filtri.filter((f) => f.colonna !== colonna),
        ...(filtro ? [filtro] : []),
      ],
    }));
    setMenuAperto(null);
  }

  function ordina(colonna: string, discendente: boolean) {
    const ordinamento: Ordinamento = { colonna, discendente };
    setRichiesta((precedente) => ({ ...precedente, pagina: 1, ordinamenti: [ordinamento] }));
    setMenuAperto(null);
  }

  const ultimaPagina = risultato
    ? Math.max(1, Math.ceil(risultato.totaleRighe / risultato.dimensione))
    : 1;

  // Gli identificativi della pagina che si sta guardando: la spunta in intestazione
  // vale su questi e non su tutto il risultato filtrato. Spuntare con un clic
  // duemila righe che non si sono viste non è una comodità, è un incidente.
  const idPagina = (risultato?.righe ?? [])
    .map((riga) => (riga['Id'] === undefined ? null : String(riga['Id'])))
    .filter((id): id is string => id !== null);

  const spuntati = new Set(selezione?.selezionati ?? []);
  const paginaTutta = idPagina.length > 0 && idPagina.every((id) => spuntati.has(id));
  const paginaInParte = !paginaTutta && idPagina.some((id) => spuntati.has(id));

  function spunta(id: string, acceso: boolean) {
    if (!selezione) return;
    selezione.onCambia(
      acceso ? [...selezione.selezionati.filter((s) => s !== id), id] : selezione.selezionati.filter((s) => s !== id),
    );
  }

  function spuntaPagina(acceso: boolean) {
    if (!selezione) return;
    const fuoriPagina = selezione.selezionati.filter((s) => !idPagina.includes(s));
    selezione.onCambia(acceso ? [...fuoriPagina, ...idPagina] : fuoriPagina);
  }

  return (
    <div className="datagrid">
      <div className="barra">
        <input
          className="ricerca"
          placeholder="Cerca…"
          defaultValue={richiesta.ricerca ?? ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const valore = (e.target as HTMLInputElement).value.trim();
              setRichiesta((p) => ({ ...p, pagina: 1, ricerca: valore || undefined }));
            }
          }}
        />
      </div>

      <ChipFiltri
        filtri={richiesta.filtri}
        colonne={colonne}
        totaleRighe={risultato?.totaleRighe ?? 0}
        righeNonFiltrate={totaleNonFiltrato}
        onRimuovi={(colonna) => applicaFiltro(colonna, null)}
        onAzzera={() => setRichiesta((p) => ({ ...p, pagina: 1, filtri: [] }))}
      />

      {errore && <p className="errore">{errore}</p>}

      <div className="tabella-contenitore">
        <table>
          <thead>
            <tr>
              {selezione && (
                <th className="spunta">
                  <input
                    type="checkbox"
                    aria-label="Spunta tutte le righe di questa pagina"
                    checked={paginaTutta}
                    ref={(elemento) => {
                      // Lo stato «alcune sì e alcune no» non si può dichiarare in JSX:
                      // è una proprietà dell'elemento, non un attributo.
                      if (elemento) elemento.indeterminate = paginaInParte;
                    }}
                    onChange={(e) => spuntaPagina(e.target.checked)}
                  />
                </th>
              )}
              {colonne.map((colonna) => {
                const attivo = richiesta.filtri.find((f) => f.colonna === colonna.nome);
                const ordinato = richiesta.ordinamenti.find((o) => o.colonna === colonna.nome);
                return (
                  <th
                    key={colonna.nome}
                    className={[
                      TIPI_NUMERICI.includes(colonna.tipo) ? 'destra' : '',
                      attivo ? 'filtrata' : '',
                    ].join(' ')}
                  >
                    <button
                      type="button"
                      className="intestazione"
                      aria-expanded={menuAperto?.colonna === colonna.nome}
                      onClick={(e) => {
                        const ancora = e.currentTarget;
                        setMenuAperto((aperto) =>
                          aperto?.colonna === colonna.nome ? null : { colonna: colonna.nome, ancora },
                        );
                      }}
                    >
                      <span className="etichetta">
                        {intestazioni[colonna.nome] ?? colonna.nome}
                        {ordinato && <i className="verso">{ordinato.discendente ? '↓' : '↑'}</i>}
                      </span>
                      {/* Il tipo dichiarato sotto l'intestazione: l'utente sa
                          cosa gli chiederà il filtro prima di aprirlo. */}
                      <span className="tipo-dato">{etichettaTipo(colonna.tipo)}</span>
                    </button>

                    {menuAperto?.colonna === colonna.nome && (
                      <MenuFiltro
                        colonna={colonna}
                        ancora={menuAperto.ancora}
                        filtroAttivo={attivo}
                        caricaValori={caricaValori(colonna.nome)}
                        onApplica={(filtro) => applicaFiltro(colonna.nome, filtro)}
                        onOrdina={(discendente) => ordina(colonna.nome, discendente)}
                        onChiudi={() => setMenuAperto(null)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {risultato?.righe.map((riga, indice) => {
              const id = riga['Id'] === undefined ? null : String(riga['Id']);
              return (
                <tr
                  key={id ?? indice}
                  className={[
                    onRigaSelezionata ? 'cliccabile' : '',
                    id !== null && id === idSelezionato ? 'selezionata' : '',
                    id !== null && spuntati.has(id) ? 'spuntata' : '',
                  ].join(' ').trim()}
                  tabIndex={onRigaSelezionata ? 0 : undefined}
                  onClick={onRigaSelezionata ? () => onRigaSelezionata(riga) : undefined}
                  onKeyDown={
                    onRigaSelezionata
                      ? (e) => {
                          // Una riga che si apre solo col mouse esclude chi lavora da tastiera,
                          // ed e' esattamente chi passa la giornata dentro una griglia.
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRigaSelezionata(riga);
                          }
                        }
                      : undefined
                  }
                >
                  {selezione && (
                    // Il clic sulla spunta non deve aprire il dettaglio: sono due gesti
                    // diversi sulla stessa riga.
                    <td className="spunta" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label="Spunta questa riga"
                        disabled={id === null}
                        checked={id !== null && spuntati.has(id)}
                        onChange={(e) => id !== null && spunta(id, e.target.checked)}
                      />
                    </td>
                  )}
                  {colonne.map((colonna) => {
                    const testo = formatta(riga[colonna.nome], colonna);
                    return (
                      <td
                        key={colonna.nome}
                        className={TIPI_NUMERICI.includes(colonna.tipo) ? 'destra' : ''}
                      >
                        {pastiglie?.includes(colonna.nome) && testo !== '' ? (
                          <span className={`pastiglia ${tonoStato(testo)}`}>{testo}</span>
                        ) : (
                          testo
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}

            {risultato?.righe.length === 0 && !inCaricamento && (
              <tr>
                <td colSpan={colonne.length + (selezione ? 1 : 0)} className="nessun-risultato">
                  Nessuna riga con questi filtri.
                </td>
              </tr>
            )}
          </tbody>

          {risultato && Object.keys(risultato.totali).length > 0 && (
            <tfoot>
              <tr>
                {selezione && <td className="spunta" />}
                {colonne.map((colonna, indice) => (
                  <td
                    key={colonna.nome}
                    className={TIPI_NUMERICI.includes(colonna.tipo) ? 'destra' : ''}
                  >
                    {/* I totali sono calcolati su tutte le righe filtrate, non sulla pagina visibile. */}
                    {colonna.nome in risultato.totali
                      ? formatta(risultato.totali[colonna.nome], colonna)
                      : indice === 0
                        ? 'Totale filtrato'
                        : ''}
                  </td>
                ))}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="paginazione">
        <button
          type="button"
          disabled={richiesta.pagina <= 1}
          onClick={() => setRichiesta((p) => ({ ...p, pagina: p.pagina - 1 }))}
        >
          ← Precedente
        </button>
        <span>
          Pagina {risultato?.pagina ?? 1} di {ultimaPagina}
        </span>
        <button
          type="button"
          disabled={richiesta.pagina >= ultimaPagina}
          onClick={() => setRichiesta((p) => ({ ...p, pagina: p.pagina + 1 }))}
        >
          Successiva →
        </button>
        {inCaricamento && <span className="attesa">…</span>}
      </div>
    </div>
  );
}

function formatta(valore: unknown, colonna: DefinizioneColonna): string {
  if (valore === null || valore === undefined) return '';

  switch (colonna.tipo) {
    case 'Importo':
      return formattaImporto(valore);
    case 'Numero':
      return formattaNumero(valore);
    case 'Percentuale':
      return formattaPercentuale(valore);
    case 'Data':
      return formattaData(valore);
    case 'DataOra':
      return formattaDataOra(valore);
    case 'Booleano':
      return valore ? 'sì' : 'no';
    default:
      return String(valore);
  }
}
